import { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedCallback } from 'use-debounce';
import {
	CaseCard,
	Typography,
	PaginationCustom,
	EmptyFiltersScreen,
	LoaderScreen,
	Icon,
	FunnelEmptyScreen,
	LabelShowingResults,
} from '@/components/common';

import { ContainerBase } from '@/components/landing/containers';
import { useClaimIcons, type BadgeTypes } from '@/hooks/useClaimIcons';
import { useInternalCaseFilter } from '@/hooks';
import { MagnifyingGlass } from '@/public/icons/tsx';

import { Combobox } from '@/components/ui/combobox';

import {
	categoriesService,
	casesService,
	GET_CASES_STATUS,
	GET_CATEGORIES,
	GET_CASES,
	GET_CASES_STATES,
} from '@/api';
import Icons from '@/const/icons';
import clsx from 'clsx';
import { GC_TIME, STALE_TIME } from '@/const/common';

interface Options {
	value: string;
	label: JSX.Element | string;
	icon?: JSX.Element;
}

interface CasesGridProps {
	className?: string;
}

export const CasesGrid: React.FC<CasesGridProps> = ({ className }) => {
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const { replace } = useRouter();

	const params = new URLSearchParams(searchParams);

	const [searchValue, setSearchValue] = useState('');

	const [category, setCategory] = useState<string | undefined>('');
	const [openCategories, setOpenCategories] = useState(false);
	const [optionsCategories, setOptionsCategories] = useState<Options[]>([]);

	const [status, setStatus] = useState<string | undefined>('');
	const [openStatus, setOpenStatus] = useState(false);
	const [optionsStatus, setOptionsStatus] = useState<Options[]>([]);

	const [state, setState] = useState<string | undefined>('');
	const [openState, setOpenState] = useState(false);
	const [optionsState, setOptionsState] = useState<Options[]>([]);

	const totalRecords = useRef(null);

	const { getIcon } = useClaimIcons();

	const { data: categoriesRes } = useQuery({
		queryKey: [GET_CATEGORIES],
		queryFn: () => categoriesService.getAll(),
		staleTime: STALE_TIME,
		gcTime: GC_TIME,
	});

	const { data: statusesRes } = useQuery({
		queryKey: [GET_CASES_STATUS],
		queryFn: () => casesService.getCasesStatus(),
		staleTime: STALE_TIME,
		gcTime: GC_TIME,
	});

	const { data: statesRes } = useQuery({
		queryKey: [GET_CASES_STATES],
		queryFn: () => casesService.getStates(),
		staleTime: STALE_TIME,
		gcTime: GC_TIME,
	});

	const searchPage = params.get('page') ? Number(params.get('page')) : 1;
	const searchStatus = status;

	const {
		data: casesRes,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [
			GET_CASES,
			searchPage,
			searchStatus,
			category,
			state,
			searchValue,
		],
		queryFn: () =>
			casesService.getAll({
				page: searchPage,
				categoryId: category ? Number(category) : undefined,
				statusId: searchStatus ? Number(searchStatus) : undefined,
				search: encodeURIComponent(searchValue),
				eligibleStateId: state ? Number(state) : undefined,
			}),
	});

	const categories = categoriesRes?.data;
	const statuses = statusesRes?.data;
	const states = statesRes?.data;

	useEffect(() => {
		if (category) {
			const labelCategory = categories?.find(
				(cat) => cat.id === Number(category)
			);
			if (labelCategory) {
				fetchAndReplace();
			}
		} else {
			fetchAndReplace();
		}
	}, [category, categories]);

	useEffect(() => {
		if (status) {
			const labelStatus = statuses?.find((stat) => stat.id === Number(status));
			if (labelStatus) {
				fetchAndReplace();
			}
		} else {
			fetchAndReplace();
		}
	}, [statuses, status]);

	useEffect(() => {
		if (state) {
			const labelState = states?.find((stat) => stat.id === Number(state));

			if (labelState) {
				fetchAndReplace();
			}
		} else {
			fetchAndReplace();
		}
	}, [state, states]);

	const handleSearch = useDebouncedCallback(() => {
		fetchAndReplace();
	}, 300);

	const fetchAndReplace = () => {
		setTimeout(() => {
			if (category || status || state) {
				params.set('page', '1');
			} else {
				params.set('page', String(searchPage));
			}
			replace(`${pathname}?${params.toString()}`);
			refetch();
		}, 50);
	};

	const { filterInternalCases } = useInternalCaseFilter();
	const casesData = filterInternalCases(casesRes?.data.items || []);
	const num = casesData.length;
	const totalPages = casesRes?.data.totalPages || 0;
	const totalItems = casesRes?.data.totalItems || 0;

	useEffect(() => {
		if (totalRecords.current === null && totalItems) {
			(totalRecords.current as number | null) = totalItems;
		}
	}, [totalItems]);

	// -------------- Fill Categories, Status and States ------------//
	useEffect(() => {
		const parsedStates: Options[] = [];
		states?.map((state) => {
			return parsedStates.push({
				value: String(state.id),
				label: state.name,
			});
		});
		setOptionsState([
			{
				label: 'Clear All',
				value: 'clear',
				icon: <Icon src={Icons.broom} className="!w-5 !h-5" />,
			},
			...parsedStates,
		]);
	}, [states]);

	useEffect(() => {
		const parsedCategories: Options[] = [];
		categories?.map((category) => {
			return parsedCategories.push({
				value: String(category.id),
				label: category.name,
				icon: getIcon(category.name as BadgeTypes),
			});
		});
		setOptionsCategories([
			{
				label: 'Clear All',
				value: 'clear',
				icon: <Icon src={Icons.broom} className="!w-5 !h-5" />,
			},
			...parsedCategories,
		]);
	}, [categories]);

	useEffect(() => {
		const parsedStatuses: Options[] = [];
		statuses?.map((status) => {
			return parsedStatuses.push({
				value: String(status.id),
				label: status.name,
			});
		});
		setOptionsStatus([
			{
				label: 'Clear All',
				value: 'clear',
				icon: <Icon src={Icons.broom} className="!w-5 !h-5" />,
			},
			...parsedStatuses,
		]);
	}, [statuses]);

	// ------------------ Handles to add or remove Clear Button -----------//
	const handleCategory = (value: string | undefined): void => {
		if (value === 'clear') {
			setCategory('');
		} else {
			setCategory(value);
		}
	};

	const handleStatus = (value: string | undefined): void => {
		if (value === 'clear') {
			setStatus('');
		} else {
			setStatus(value);
		}
	};

	const handleState = (value: string | undefined): void => {
		if (value === 'clear') {
			setState('');
		} else {
			setState(value);
		}
	};

	return (
		<ContainerBase className={clsx('relative z-10', className)}>
			<div className="flex flex-col md:flex-row items-baseline xl:items-center justify-between pt-10 pb-10">
				<div className="flex flex-wrap gap-6">
					<div className="flex items-center bg-[#F4F4F9] rounded-[100px] py-2 px-4 gap-2 w-full md:w-[283px]">
						<MagnifyingGlass />
						<input
							id="cases-search-input"
							type="text"
							value={searchValue}
							className="outline-none bg-transparent w-[90%] !text-[16px]"
							placeholder="Search"
							onChange={(e) => {
								handleSearch();
								setSearchValue(e.target.value);
							}}
						/>
					</div>
					<div className="flex flex-col md:flex-row items-start md:items-center w-full md:w-auto gap-1 md:gap-2 z-0">
						<Combobox
							value={category}
							options={optionsCategories}
							open={openCategories}
							placeholder="Category"
							typographyTypeLabel="regular-16"
							classNameTrigger="!h-[33px]"
							classNamePopoverContent="!w-auto max-h-[400px]"
							setOpen={(value) => setOpenCategories(value)}
							setValue={(value) => handleCategory(value)}
						/>
						<Combobox
							value={status}
							open={openStatus}
							options={optionsStatus}
							placeholder="Cases Status"
							typographyTypeLabel="regular-16"
							classNameTrigger="!h-[33px]"
							classNamePopoverContent="!w-[214px]"
							setOpen={(value) => setOpenStatus(value)}
							setValue={(value) => handleStatus(value)}
						/>
						<Combobox
							isSearch
							open={openState}
							placeholder="Eligible State"
							options={optionsState}
							value={state}
							typographyTypeLabel="regular-16"
							classNameTrigger="!h-[33px]"
							setOpen={(value) => setOpenState(value)}
							setValue={(value) => handleState(value)}
							classNameCommandList="!max-h-[336px] overflow-y-scroll"
						/>
					</div>
				</div>
				<div className="w-full xl:w-auto xl:min-w-[220px] flex justify-end pt-3 md:pt-0">
					<LabelShowingResults
						totalItems={totalItems}
						total={num}
						labelTotalItems="cases"
						typographyType="regular-16"
						isSearched={
							!!searchStatus || !!searchValue || !!category || !!state
						}
					/>
				</div>
			</div>
			<div
				className={clsx('min-h-screen', {
					'min-h-0': !isLoading,
				})}
			>
				{isLoading && <LoaderScreen className="mt-20 2xl:mt-40" />}
				{!isLoading && casesData.length === 0 && !searchValue && (
					<FunnelEmptyScreen
						message="There are currently no cases matching the selected filters"
						className="pt-8"
					/>
				)}

				{!isLoading && casesData.length === 0 && searchValue && (
					<EmptyFiltersScreen>
						<>
							<Typography type="regular-18">
								We couldn&apos;t find a case for &quot;{searchValue}&quot;.
							</Typography>
							<br />
							<Typography type="regular-18">
								Please try again with a different word.
							</Typography>
						</>
					</EmptyFiltersScreen>
				)}
				{!isLoading && casesData.length > 0 && (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-6 mb-6">
						{casesData.map((item, index) => (
							<CaseCard
								key={index}
								{...item}
								classNameButton="!h-[48px] !min-w-[133px]"
							/>
						))}
					</div>
				)}
			</div>
			{!isLoading && casesData.length > 0 && (
				<div className="md:mt-10">
					<PaginationCustom totalPages={totalPages} />
				</div>
			)}
		</ContainerBase>
	);
};

interface ClearButtonProps {
	className?: string;
}

export const ClearButton: React.FC<ClearButtonProps> = ({ className }) => {
	return (
		<Typography type="regular-16" className={className}>
			Clear All
		</Typography>
	);
};
